import React, { useEffect, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ArrowRight } from 'lucide-react';
import { api, mediaUrl } from '@/lib/api';

const DISMISS_KEY = (id, freq) => `pg_marketing_dismissed::${id}::${freq}`;
const DAY_MS = 24 * 60 * 60 * 1000;

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isDismissed(p) {
  if (!p || !p.id) return false;
  const freq = p.frequency || 'always';
  if (freq === 'always') return false;
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY(p.id, 'session'));
    if (raw && freq === 'once_per_session') return true;
  } catch (e) { /* noop */ }
  try {
    const raw = localStorage.getItem(DISMISS_KEY(p.id, 'day'));
    if (raw && freq === 'once_per_day') {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.day === todayKey()) return true;
      if (parsed && parsed.ts && Date.now() - parsed.ts < DAY_MS) return true;
    }
  } catch (e) { /* noop */ }
  return false;
}

function markDismissed(p) {
  if (!p || !p.id) return;
  const freq = p.frequency || 'always';
  try {
    if (freq === 'once_per_session') {
      sessionStorage.setItem(DISMISS_KEY(p.id, 'session'), '1');
    } else if (freq === 'once_per_day') {
      localStorage.setItem(DISMISS_KEY(p.id, 'day'), JSON.stringify({ day: todayKey(), ts: Date.now() }));
    }
  } catch (e) { /* noop */ }
}

function accentForPlacement(p) {
  if (!p) return 'emerald';
  if (p.placement === 'banner') return 'gold';
  return 'pink';
}

function accentToVar(accent) {
  if (accent === 'emerald') return { glow: 'var(--pg-emerald)', text: 'var(--pg-emerald)', shadow: 'rgba(25,194,142,0.35)' };
  if (accent === 'gold') return { glow: 'var(--pg-gold-2)', text: 'var(--pg-gold-2)', shadow: 'rgba(226,199,122,0.35)' };
  return { glow: 'var(--pg-neon-pink)', text: 'var(--pg-neon-pink)', shadow: 'rgba(255,62,165,0.35)' };
}

function aspectStyle(ratio) {
  switch (ratio) {
    case 'poster_2_3':
      return { className: 'aspect-[2/3]', objectFit: 'cover', background: 'var(--pg-navy-800)' };
    case 'full_auto':
      // No fixed aspect; image renders at intrinsic size. Add a max height + contain so it
      // is fully visible without cropping. Cap with a sensible max to avoid pushing CTA off screen.
      return { className: 'max-h-[60vh] flex items-center justify-center', objectFit: 'contain', background: 'var(--pg-navy-900)' };
    case 'square_1_1':
    default:
      return { className: 'aspect-square', objectFit: 'cover', background: 'var(--pg-navy-800)' };
  }
}

/* =====================================================
   Inline hero card — rendered above the channel grid
   ===================================================== */
export function MarketingHeroCard({ poster }) {
  if (!poster) return null;
  const accent = accentForPlacement({ ...poster, placement: 'home_hero' });
  const c = accentToVar(accent);
  const ratio = poster.aspect_ratio || 'square_1_1';
  const a = aspectStyle(ratio);
  return (
    <motion.section
      data-testid="marketing-home-hero"
      initial={{ opacity: 1, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-2"
    >
      <div className="pg-card overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
          {poster.image_url && (
            <div
              className={`md:col-span-5 overflow-hidden ${a.className}`}
              style={{ background: a.background }}
              data-testid="marketing-image-frame"
              data-aspect={ratio}
            >
              <img
                src={mediaUrl(poster.image_url)}
                alt={poster.title}
                className="w-full h-full"
                style={{ objectFit: a.objectFit }}
              />
            </div>
          )}
          <div className={`${poster.image_url ? 'md:col-span-7' : 'md:col-span-12'} p-6 sm:p-8 flex flex-col gap-3 justify-center`}>
            <p className="font-mono text-[10px] tracking-[0.32em] uppercase" style={{ color: c.text, textShadow: `0 0 16px ${c.shadow}` }}>FEATURED</p>
            <h3 className="font-display text-2xl sm:text-3xl tracking-[0.04em] text-[var(--pg-ink)] leading-tight">{poster.title}</h3>
            {poster.subtitle && <p className="text-sm text-[var(--pg-ink-muted)]">{poster.subtitle}</p>}
            {poster.body && <p className="text-sm text-[var(--pg-ink)] leading-relaxed line-clamp-3 whitespace-pre-line">{poster.body}</p>}
            {poster.cta_label && poster.cta_url && (
              <div className="mt-2">
                <a href={poster.cta_url} className="pg-btn-primary inline-flex items-center gap-2" data-testid="marketing-home-hero-cta">
                  {poster.cta_label} <ArrowRight size={14} />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.section>
  );
}

/* =====================================================
   Slim sticky banner at top of viewport
   ===================================================== */
export function MarketingBanner({ poster, onDismiss }) {
  if (!poster) return null;
  const c = accentToVar(accentForPlacement(poster));
  return (
    <motion.div
      data-testid="marketing-banner"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -40, opacity: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 inset-x-0 z-[60] border-b border-[var(--pg-border-2)] backdrop-blur-xl"
      style={{ background: 'rgba(7,11,20,0.84)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-2.5 flex items-center gap-3">
        <span className="font-mono text-[10px] tracking-[0.32em] uppercase shrink-0" style={{ color: c.text }}>★</span>
        <div className="min-w-0 flex-1 flex items-baseline gap-2 flex-wrap">
          <span className="font-display text-sm tracking-[0.18em] text-[var(--pg-ink)] truncate">{poster.title}</span>
          {poster.subtitle && <span className="text-xs text-[var(--pg-ink-muted)] truncate">{poster.subtitle}</span>}
        </div>
        {poster.cta_label && poster.cta_url && (
          <a href={poster.cta_url} className="pg-btn-ghost rounded-full !px-3 !py-1.5 text-[10px] font-mono tracking-[0.24em] uppercase inline-flex items-center gap-1 shrink-0" data-testid="marketing-banner-cta">
            {poster.cta_label} <ArrowRight size={12} />
          </a>
        )}
        <button
          onClick={onDismiss}
          aria-label="Dismiss banner"
          data-testid="marketing-banner-close"
          className="p-1.5 rounded-md text-[var(--pg-ink-faint)] hover:text-[var(--pg-ink)] shrink-0"
        >
          <X size={14} />
        </button>
      </div>
    </motion.div>
  );
}

/* =====================================================
   Centered popup modal — shows after 5s delay
   ===================================================== */
export function MarketingPopup({ poster, onDismiss, autoShowDelayMs = 5000, immediate = false }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!poster) return undefined;
    if (immediate) {
      setVisible(true);
      return undefined;
    }
    const id = setTimeout(() => setVisible(true), autoShowDelayMs);
    return () => clearTimeout(id);
  }, [poster, autoShowDelayMs, immediate]);

  const close = () => {
    setVisible(false);
    onDismiss && onDismiss();
  };

  if (!poster) return null;
  const c = accentToVar(accentForPlacement({ ...poster, placement: 'popup' }));
  const ratio = poster.aspect_ratio || 'square_1_1';
  const a = aspectStyle(ratio);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="marketing-popup"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={close}
        >
          <div className="absolute inset-0 bg-[rgba(2,4,10,0.78)] backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 8 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto pg-card border border-[var(--pg-border-2)]"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: `0 30px 80px -20px ${c.shadow}` }}
          >
            <button
              onClick={close}
              aria-label="Close"
              data-testid="marketing-popup-close"
              className="absolute top-3 right-3 z-10 p-2 rounded-full bg-[rgba(15,10,22,0.7)] text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] backdrop-blur-md"
            >
              <X size={14} />
            </button>
            {poster.image_url && (
              <div
                className={`overflow-hidden ${a.className}`}
                style={{ background: a.background }}
                data-testid="marketing-image-frame"
                data-aspect={ratio}
              >
                <img
                  src={mediaUrl(poster.image_url)}
                  alt={poster.title}
                  className="w-full h-full"
                  style={{ objectFit: a.objectFit }}
                />
              </div>
            )}
            <div className="p-6 sm:p-7">
              <p className="font-mono text-[10px] tracking-[0.32em] uppercase" style={{ color: c.text, textShadow: `0 0 18px ${c.shadow}` }}>A NOTE FROM G</p>
              <h3 className="font-display text-2xl sm:text-3xl mt-2 tracking-[0.04em] text-[var(--pg-ink)] leading-tight" data-testid="marketing-popup-title">{poster.title}</h3>
              {poster.subtitle && <p className="text-sm text-[var(--pg-ink-muted)] mt-2">{poster.subtitle}</p>}
              {poster.body && <p className="text-sm text-[var(--pg-ink)] mt-4 leading-relaxed whitespace-pre-line">{poster.body}</p>}
              {poster.cta_label && poster.cta_url && (
                <div className="mt-5 flex items-center gap-3">
                  <a
                    href={poster.cta_url}
                    onClick={() => markDismissed(poster)}
                    className="pg-btn-primary inline-flex items-center gap-2"
                    data-testid="marketing-popup-cta"
                  >
                    {poster.cta_label} <ArrowRight size={14} />
                  </a>
                  <button
                    onClick={close}
                    className="text-[11px] font-mono tracking-[0.24em] text-[var(--pg-ink-faint)] hover:text-[var(--pg-ink)] uppercase"
                  >
                    Maybe later
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* =====================================================
   Smart wrapper — chooses the first active poster per placement
   Renders banner + popup + home_hero card.
   ===================================================== */
export function MarketingActive({ enabled = true, onHeroPoster, popupDelayMs = 5000 }) {
  const [posters, setPosters] = useState([]);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancel = false;
    api.get('/marketing/active').then(({ data }) => {
      if (!cancel) setPosters(Array.isArray(data) ? data : []);
    }).catch(() => {});
    return () => { cancel = true; };
  }, [enabled]);

  const banner = posters.find((p) => p.placement === 'banner' && !isDismissed(p));
  const popup = posters.find((p) => p.placement === 'popup' && !isDismissed(p));
  const hero = posters.find((p) => p.placement === 'home_hero');

  useEffect(() => {
    if (onHeroPoster) onHeroPoster(hero || null);
  }, [hero, onHeroPoster]);

  const handleBannerDismiss = useCallback(() => {
    if (banner) markDismissed(banner);
    setBannerDismissed(true);
  }, [banner]);

  const handlePopupDismiss = useCallback(() => {
    if (popup) markDismissed(popup);
    setPopupDismissed(true);
  }, [popup]);

  return (
    <>
      <AnimatePresence>
        {banner && !bannerDismissed && <MarketingBanner key={banner.id} poster={banner} onDismiss={handleBannerDismiss} />}
      </AnimatePresence>
      {popup && !popupDismissed && <MarketingPopup poster={popup} onDismiss={handlePopupDismiss} autoShowDelayMs={popupDelayMs} />}
    </>
  );
}
